import { motion } from "framer-motion";
import { fadeUp, Eyebrow, TextCta, VIEWPORT } from "./primitives";
import { useLanguage } from "@/i18n/LanguageContext";

/** Un mouvement du raisonnement : label doré, puis le texte. */
const Movement = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <p className="text-[11px] tracking-[0.3em] uppercase text-gold-ink mb-3">{label}</p>
    <p className="text-base md:text-lg text-ink-muted leading-relaxed">{children}</p>
  </div>
);

/**
 * Constat, besoin, réponse : trois mouvements courts.
 *
 * Remplace trois mises en situation qui décrivaient l'angoisse du visiteur
 * avant de lui montrer quoi que ce soit. Ce qu'elles portaient (acheteur,
 * propriétaire, marché) appartient à la section « Pour qui », écrit à
 * l'endroit : ce que Provena fait pour chacun.
 */
export const ProblemSection = ({ registerTo }) => {
  const { t } = useLanguage();
  return (
    <section
      id="probleme"
      data-testid="problem-section"
      className="relative scroll-mt-28 py-16 md:py-24 border-t border-ink/5 bg-surface-muted overflow-hidden"
    >
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={VIEWPORT}
        variants={fadeUp}
        className="relative max-w-3xl mx-auto px-5 md:px-12"
      >
        <Eyebrow>{t("problem.eyebrow")}</Eyebrow>
        <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.6rem] leading-[1.15] tracking-tight mt-4 mb-6">
          {t("problem.title")}
        </h2>

        <p className="text-base md:text-lg text-ink-muted leading-relaxed">
          {t("problem.constat")}
        </p>

        <div className="mt-10 space-y-8 border-l border-gold/30 pl-6 md:pl-8">
          <Movement label={t("problem.needLabel")} testId="problem-need">
            {t("problem.need")}
          </Movement>
          <Movement label={t("problem.answerLabel")} testId="problem-answer">
            {t("problem.answer")}
          </Movement>
        </div>

        <div className="mt-10">
          <TextCta to={registerTo} testId="problem-cta" track="problem">
            {t("problem.cta")}
          </TextCta>
        </div>

        {/* La source est ce qui rend le chiffre recevable par un professionnel. */}
        <p data-testid="problem-source" className="mt-8 text-[11px] text-ink-faint leading-relaxed">
          {t("problem.sourcePre")}{" "}
          <a
            href="https://www.thewatchregister.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-ink-subtle underline underline-offset-2 hover:text-gold-ink transition-colors"
          >
            {t("problem.sourceName")}
          </a>{" "}
          · {t("problem.sourceNote")} (2025).
        </p>
      </motion.div>
    </section>
  );
};

export default ProblemSection;
